import React from 'react'
import type { Image, Video, Audio } from '@mission.io/mission-toolkit/actions'
import classnames from 'classnames'
import Dialog from '../../../../../basics/Dialog'

import { ControllableVideo } from '../../MediaPopup'
import {
	BsArrowsAngleContract as ArrowsContract,
	BsArrowsAngleExpand as ArrowsExpand,
} from 'react-icons/bs'

/**
 * Media event input is a component for displaying media without text during a literacy event.
 * @param {Image|Video|Audio} props.media the media that will be displayed
 * @param {string} props.title the title of the media
 * @returns {React$Node}
 */
export function MediaEventInput({
	media,
	title,
	controlModal,
	className,
}: {
	media: Image | Video | Audio,
	title?: string,
	className?: string,
	controlModal?: {
		isOpen: boolean,
		setIsOpen: (boolean | (boolean => boolean)) => void,
	},
}): React$Node {
	if (media.type === 'IMAGE') {
		return (
			<div className={classnames(className, 'relative flex-1 overflow-hidden')}>
				{/* Title is purposefully left off of the image media so that an image can be fully seen. */}
				<img
					alt="Event Alert"
					className="max-w-full max-h-full inset-center"
					src={media.url}
				/>
				<ContentExpander
					controlModal={controlModal}
					className="bottom-2 right-2">
					<img
						alt="Event Alert"
						className="border-4 border-game-primary-green rounded-lg max-w-full max-h-full"
						src={media.url}
					/>
				</ContentExpander>
			</div>
		)
	} else if (media.type === 'VIDEO') {
		return (
			<div
				className={classnames(
					className,
					'relative flex flex-col w-full items-center justify-start h-[calc(100%-var(--spacing2x))]'
				)}>
				{title && <Header title={title} />}
				<ControllableVideo
					src={media.url}
					className="max-w-full max-h-full flex-1 overflow-hidden"
					autoPlay={false}
				/>
			</div>
		)
	} else if (media.type === 'AUDIO') {
		return (
			<div
				className={classnames(
					className,
					'relative flex flex-col w-full items-center justify-center h-[calc(100%-var(--spacing2x))]'
				)}>
				{title && <Header title={title} />}
				<audio className="width-full" controls>
					<source src={media.url} />
				</audio>
			</div>
		)
	}
}

const Header = ({ title }: { title: string }) => {
	return title ? <h3 className="m-0 ml-4">{title}</h3> : null
}

/**
 * A component that displays an icon in the bottom right corner of its next relative ancestor.
 * When clicked, it will open a modal with the children passed to it.
 * @param {React$Node} props.children the content to display in the modal.
 * @param {string|void} props.className the class name to apply to the icon.
 * @returns {React$Node}
 */
export function ContentExpander({
	children,
	className,
	controlModal,
	onExpand,
	modalStyles = {},
}: {
	children?: React$Node,
	className?: string,
	controlModal?: {
		isOpen: boolean,
		setIsOpen: (boolean | (boolean => boolean)) => void,
	},
	onExpand?: () => void,
	modalStyles?: {
		overlay?: any,
		content?: any,
	},
}): React$Node {
	const [_expanded, _setExpanded] = React.useState(false)
	const expanded = controlModal ? controlModal.isOpen : _expanded
	const setExpanded = controlModal ? controlModal.setIsOpen : _setExpanded

	return (
		<>
			<ExpandIcon
				className={classnames(className, 'size-8')}
				onClick={() => {
					onExpand && onExpand()
					setExpanded(state => !state)
				}}
				expanded={expanded}
			/>

			{expanded && (
				<Dialog
					onRequestClose={event => {
						event.stopPropagation()
						setExpanded(false)
					}}
					style={{
						overlay: { ...ModalStyles.overlay, ...modalStyles.overlay },
						content: { ...ModalStyles.content, ...modalStyles.content },
					}}
					isOpen={true}>
					{children}
				</Dialog>
			)}
		</>
	)
}

const ModalStyles = {
	overlay: {
		backgroundColor: 'rgba(0, 0, 0, 0.6)',
		zIndex: 20,
	},
	content: {
		border: 'none',
		top: '45%',
		left: '50%',
		right: 'auto',
		bottom: 'auto',
		marginRight: '-50%',
		transform: 'translate(-50%, -50%)',
		backgroundColor: 'transparent',
		overflow: 'hidden',
		minWidth: '0px',
		maxWidth: '100%',
		minHeight: 'fit',
		padding: '0',
		margin: '8px',
	},
}

function ExpandIcon({
	onClick,
	className,
	expanded,
}: {
	onClick: (e: MouseEvent) => void,
	className?: string,
	expanded?: boolean,
}): React$Node {
	const iconProps = {
		color: 'white',
		style: { width: '80%', height: '80%' },
	}

	return (
		<button
			className={classnames(
				'text-white hover:scale-110 transition-all duration-200 ease-in-out',
				'z-[--layer-panels] rotate-90 absolute bottom-2 right-2 rounded-full !bg-game-primary-700 size-12 aspect-square !p-2 flex justify-center items-center',
				className
			)}
			onClick={e => {
				e.stopPropagation()
				onClick(e)
			}}
			aria-label={expanded ? 'Contract' : 'Expand'}>
			{expanded ? (
				<ArrowsContract {...iconProps} />
			) : (
				<ArrowsExpand {...iconProps} />
			)}
		</button>
	)
}
